import type {
  Control,
  FieldPath,
  FieldValues,
  Path,
  UseFormReturn,
} from 'react-hook-form';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { z } from 'zod';

import type {
  PosFormItemControlledComponentProps,
  PosFormItemUncontrolledComponentProps,
} from './PosFormItem';

export const createFormFields = <T extends FieldValues>(
  form: UseFormReturn<T>
) => {
  return Object.keys(form.getValues()).map((key) => ({
    name: key,
    value: form.watch(key as Path<T>),
  }));
};

// Prevents console errors by extracting only props that exist on PosFormItem
export const PosFormItemKeysEnum = z.enum([
  'control',
  'name',
  'help',
  'required',
  'validateStatus',
  'noMarginOnValid',
  'boldLabel',
  'valuePropName',
  'colon',
  'label',
  'labelAlign',
  'labelCol',
  'noStyle',
  'tooltip',
  'formItemProps',
]);

type PosFormItemKeys = z.infer<typeof PosFormItemKeysEnum>;

type ExtractPosFormItemProps<T> = {
  posFormItemProps: Pick<T, Extract<PosFormItemKeys, keyof T>>;
  rest: Omit<T, Extract<PosFormItemKeys, keyof T>>;
};
/** Extracts `PosFormItemProps` type props from props */
export const extractPosFormItemProps = <T extends object>(
  props: T
): ExtractPosFormItemProps<T> => {
  return {
    posFormItemProps: pick(props, PosFormItemKeysEnum.options) as Pick<
      T,
      Extract<PosFormItemKeys, keyof T>
    >,
    rest: omit(props, PosFormItemKeysEnum.options) as Omit<
      T,
      Extract<PosFormItemKeys, keyof T>
    >,
  };
};

type ControlledComponentProps<TFieldValues extends FieldValues = FieldValues> =
  PosFormItemControlledComponentProps<TFieldValues> & {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    value?: never;
    onChange?: never;
  };

type UncontrolledComponentProps<ValueType, OnChangeType> =
  PosFormItemUncontrolledComponentProps & {
    control?: never;
    name?: string;
    value?: ValueType;
    onChange?: OnChangeType;
  };

/** Creates an RHF wrapper type union for components that are wrapped in `PosFormItem.tsx` */
export type PosFormComponentType<
  BaseProps,
  TFieldValues extends FieldValues = FieldValues,
  ValueType = BaseProps extends { value?: infer H } ? H : unknown,
  OnChangeType = BaseProps extends { onChange?: infer H } ? H : unknown,
> = Omit<BaseProps, 'name' | 'value' | 'onChange'> &
  (
    | ControlledComponentProps<TFieldValues>
    | UncontrolledComponentProps<ValueType, OnChangeType>
  );
